import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get CSRF token from cookie
const getCSRFToken = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf_token') {
      return decodeURIComponent(value);
    }
  }
  return null;
};

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  // Cookie-based auth: send httpOnly access_token cookie automatically.
  // Backend sets `Access-Control-Allow-Credentials: true` and a specific origin.
  withCredentials: true,
});

// Add auth token and CSRF token to requests
api.interceptors.request.use((config) => {
  // Backward-compat: also send legacy localStorage token as Bearer.
  // Backend prefers httpOnly cookie when both are present.
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Add CSRF token for state-changing requests (POST, PUT, PATCH, DELETE)
  const method = config.method?.toUpperCase();
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }
  
  return config;
});

// Handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // If unauthorized, clear token and redirect
    if (error.response?.status === 401) {
      // Token might be expired, but don't auto-logout
      console.debug('Auth error:', error.response?.data?.detail);
    }
    return Promise.reject(error);
  }
);

// Packages
export const getPackages = async (params = {}) => {
  const response = await api.get('/packages', { params });
  return response.data;
};

export const getFeaturedPackages = async () => {
  const response = await api.get('/packages/featured');
  return response.data;
};

export const getPackageById = async (id) => {
  const response = await api.get(`/packages/${id}`);
  return response.data;
};

export const getOperators = async () => {
  const response = await api.get('/operators');
  return response.data;
};

// Redirect logging
export const logRedirect = async (packageId, packageTitle, operator, category = '', sourcePage = 'homepage') => {
  const response = await api.post('/redirect-log', null, {
    params: { 
      package_id: packageId, 
      package_title: packageTitle, 
      operator,
      category,
      source_page: sourcePage
    }
  });
  return response.data;
};

// Chat
export const sendChatMessage = async (message, sessionId) => {
  const response = await api.post('/chat', { message, session_id: sessionId });
  return response.data;
};

export const getChatHistory = async (sessionId) => {
  const response = await api.get(`/chat/history/${sessionId}`);
  return response.data;
};

// Auth
export const register = async (email, password, name) => {
  const response = await api.post('/auth/register', { email, password, name });
  return response.data;
};

export const login = async (email, password) => {
  const response = await api.post('/auth/login', { email, password });
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

// Dashboard (Super Admin only)
export const getDashboardStats = async (days = 30) => {
  const response = await api.get('/dashboard/stats', { params: { days } });
  return response.data;
};

export const getRecentClicks = async (limit = 100, offset = 0, days = 30) => {
  const response = await api.get('/dashboard/recent-clicks', { params: { limit, offset, days } });
  return response.data;
};

export const exportClicksCSV = async (days = 30) => {
  const response = await api.get('/dashboard/export', { 
    params: { days },
    responseType: 'blob'
  });
  return response.data;
};

// Seed data
export const seedData = async () => {
  const response = await api.post('/seed');
  return response.data;
};

// Usage & Payments
export const getUsage = async (sessionId) => {
  const response = await api.get('/usage', { params: { session_id: sessionId } });
  return response.data;
};

export const getPlans = async () => {
  const response = await api.get('/plans');
  return response.data;
};

export const createCheckout = async (packageId, paymentType, originUrl, sessionId) => {
  const response = await api.post('/payments/checkout', {
    package_id: packageId,
    payment_type: paymentType,
    origin_url: originUrl,
    session_id: sessionId
  });
  return response.data;
};

export const getPaymentStatus = async (stripeSessionId) => {
  const response = await api.get(`/payments/status/${stripeSessionId}`);
  return response.data;
};

export default api;
